import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { ExternalDagsterTransforms } from 'libs/end-to-end/src/external-dagster-transforms';
import { OrganizationEntitledDagsterTransform } from 'libs/end-to-end/src/external-dagster-transforms';
import { OrganizationEntitledDagsterTransforms } from 'libs/end-to-end/src/external-dagster-transforms';
import { GrantOrganizationDagsterTransformAccessRequest } from 'libs/end-to-end/src/external-dagster-transforms';
import { EmptyObject } from 'libs/end-to-end/src/generic';

interface OrganizationDagsterTransformSectionProps {
  organizationId: string;
}

export const OrganizationDagsterTransformSection: React.FC<
  OrganizationDagsterTransformSectionProps
> = ({ organizationId }) => {
  const { userAuthenticatedApiCall } = useUser();

  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);
  const [selectedTransformId, setSelectedTransformId] = useState('');
  const [isGranting, setIsGranting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const getEntitlementsUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/dagster-transforms`;
  }, [organizationId]);

  const entitlementsReadPattern = useSimpleReadPattern({
    getUrlFn: getEntitlementsUrlFn,
    canRead: true,
    responseSchema: OrganizationEntitledDagsterTransforms,
  });

  const getAllTransformsUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/external-dagster-transforms`;
  }, []);

  const allTransformsReadPattern = useSimpleReadPattern({
    getUrlFn: getAllTransformsUrlFn,
    canRead: true,
    responseSchema: ExternalDagsterTransforms,
  });

  const refreshEntitlements = useCallback(() => {
    if (
      entitlementsReadPattern.status === 'done' ||
      entitlementsReadPattern.status === 'error'
    ) {
      entitlementsReadPattern.reloadEntity();
    }
  }, [entitlementsReadPattern]);

  const availableTransforms = useMemo(() => {
    if (
      allTransformsReadPattern.status !== 'done' ||
      !('entity' in allTransformsReadPattern) ||
      entitlementsReadPattern.status !== 'done' ||
      !('entity' in entitlementsReadPattern)
    ) {
      return [];
    }

    const grantedTransformIds = new Set(
      entitlementsReadPattern.entity.map((e) => e.transformId),
    );
    return allTransformsReadPattern.entity.filter(
      (transform) => !grantedTransformIds.has(transform.id),
    );
  }, [allTransformsReadPattern, entitlementsReadPattern]);

  const getTransformName = useCallback(
    (transformId: string) => {
      if (
        allTransformsReadPattern.status !== 'done' ||
        !('entity' in allTransformsReadPattern)
      ) {
        return transformId;
      }
      const transform = allTransformsReadPattern.entity.find(
        (t) => t.id === transformId,
      );
      return transform?.name || transformId;
    },
    [allTransformsReadPattern],
  );

  const openGrantModal = useCallback(() => {
    setSelectedTransformId('');
    setErrorMessage(null);
    setIsGrantModalOpen(true);
  }, []);

  const closeGrantModal = useCallback(() => {
    setIsGrantModalOpen(false);
    setSelectedTransformId('');
    setErrorMessage(null);
  }, []);

  const grantAccess = useAsyncCallback(
    async (isAlive) => {
      if (!isAlive() || !selectedTransformId) return;

      setIsGranting(true);
      setErrorMessage(null);

      try {
        const request: GrantOrganizationDagsterTransformAccessRequest = {
          organizationId,
          dagsterTransformId: selectedTransformId,
        };

        const result = await userAuthenticatedApiCall({
          responseSchema: OrganizationEntitledDagsterTransform,
          url: `${getApiBaseUrl()}/external-dagster-transforms/grant-access`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(request),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        });

        if (!result) {
          throw new Error('Failed to grant access');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) || 'Failed to grant access',
          );
        }

        refreshEntitlements();
        closeGrantModal();
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to grant access',
        );
      } finally {
        setIsGranting(false);
      }
    },
    [
      selectedTransformId,
      organizationId,
      userAuthenticatedApiCall,
      refreshEntitlements,
      closeGrantModal,
    ],
  );

  const revokeAccess = useAsyncCallback(
    async (isAlive, entitlement: OrganizationEntitledDagsterTransform) => {
      if (!isAlive()) return;

      if (
        !confirm(
          'Are you sure you want to revoke access to this dagster transform?',
        )
      ) {
        return;
      }

      try {
        const result = await userAuthenticatedApiCall({
          responseSchema: EmptyObject,
          url: `${getApiBaseUrl()}/external-dagster-transforms/revoke-access?organizationId=${entitlement.organizationId}&dagsterTransformId=${entitlement.transformId}`,
          requestOptions: {
            method: 'POST',
          },
        });

        if (!result) {
          throw new Error('Failed to revoke access');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) || 'Failed to revoke access',
          );
        }

        refreshEntitlements();
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to revoke access',
        );
      }
    },
    [userAuthenticatedApiCall, refreshEntitlements],
  );

  const entitlementsColumnDefinitions = useMemo(
    () => [
      {
        id: 'transform',
        header: () => <div>Dagster Transform</div>,
        cell: ({ item }: { item: OrganizationEntitledDagsterTransform }) => (
          <div>{item.name}</div>
        ),
      },
      {
        id: 'actions',
        header: () => <div>Actions</div>,
        cell: ({ item }: { item: OrganizationEntitledDagsterTransform }) => (
          <Button
            behavior={{
              type: 'onClick',
              onClick: () => revokeAccess(item),
            }}
            text="Revoke Access"
            size="small"
            variant="outline"
          />
        ),
      },
    ],
    [getTransformName, revokeAccess],
  );

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <S2Typography
          variant="headline-6-alt"
          translatedContent="Dagster Transform Access"
        />
        <Button
          behavior={{
            type: 'onClick',
            onClick: openGrantModal,
          }}
          text="Grant Access to Transform"
          size="small"
        />
      </div>

      {errorMessage && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-700">
          {errorMessage}
        </div>
      )}

      <Table
        items={
          entitlementsReadPattern.status === 'done' &&
          'entity' in entitlementsReadPattern
            ? entitlementsReadPattern.entity
            : []
        }
        columnDefinitions={entitlementsColumnDefinitions}
        isLoading={entitlementsReadPattern.status === 'loading'}
        empty={
          <div className="text-center py-8 text-gray-500">
            No dagster transforms have been granted access yet
          </div>
        }
      />

      <Transition appear show={isGrantModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeGrantModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Grant Dagster Transform Access
                  </Dialog.Title>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Dagster Transform
                    </label>
                    {allTransformsReadPattern.status === 'loading' && (
                      <p className="text-sm text-gray-500 mt-2">
                        Loading available transforms...
                      </p>
                    )}
                    {allTransformsReadPattern.status === 'error' && (
                      <p className="text-sm text-red-600 mt-2">
                        Error loading transforms. Please try again.
                      </p>
                    )}
                    {allTransformsReadPattern.status === 'done' &&
                      'entity' in allTransformsReadPattern && (
                        <>
                          {availableTransforms.length > 0 ? (
                            <select
                              value={selectedTransformId}
                              onChange={(e) =>
                                setSelectedTransformId(e.target.value)
                              }
                              className="form-select block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                            >
                              <option value="">Select a transform...</option>
                              {availableTransforms.map((transform) => (
                                <option key={transform.id} value={transform.id}>
                                  {transform.name} ({transform.tableName})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <p className="text-sm text-gray-500 mt-2">
                              All available dagster transforms have been granted
                              access.
                            </p>
                          )}
                        </>
                      )}
                  </div>

                  <div className="mt-6 flex justify-end gap-2">
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: closeGrantModal,
                      }}
                      text="Cancel"
                      variant="outline"
                    />
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: grantAccess,
                      }}
                      text="Grant Access"
                      disabled={isGranting || !selectedTransformId}
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};
